import React, { useMemo, useState } from 'react';
import { Swords, TrendingUp, TrendingDown, Filter, Clock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { SectionHeader, Card, ChartTooltip, MetricTile, Insight, EmptyState, CHART_COLORS } from './shared';
import { isLaneWon } from '../../lib/analytics';
import { getChampionIconUrl } from '../../lib/champions';

export default function LaneComparisonSection({ games, champions, version, userSettings }) {
  const [filterMyChampion, setFilterMyChampion] = useState('all');
  const [filterOppChampion, setFilterOppChampion] = useState('all');
  const [filterResult, setFilterResult] = useState('all');
  // V6.1 : filtre fenêtre lane
  const [windowMinute, setWindowMinute] = useState('full'); // 'full' | '10' | '15' | '20'

  const role = (userSettings?.primary_role || 'ADC').toUpperCase();

  // Labels adaptatifs selon le rôle
  const ROLE_LABELS = {
    ADC:     { screen: 'Lane vs ADC',    opponent: 'ADC adverse',      laneWin: 'CS + Dmg supérieur' },
    MID:     { screen: 'Lane phase Mid', opponent: 'Mid adverse',       laneWin: 'Score pondéré (CS/XP/Gold/Kills)' },
    TOP:     { screen: 'Lane phase Top', opponent: 'Top adverse',       laneWin: 'Score pondéré (CS/XP/Gold/Kills)' },
    JUNGLE:  { screen: 'Early game',     opponent: 'Jungler adverse',   laneWin: 'Gold + XP diff à 15min' },
    SUPPORT: { screen: 'Lane phase Bot', opponent: 'Support adverse',   laneWin: 'Kill participation' },
  };
  const roleLabel = ROLE_LABELS[role] || ROLE_LABELS.ADC;

  // Liste des champions joués et adverses
  const myChampions = useMemo(() => {
    const set = new Set(games.filter(g => g.opponentStats).map(g => g.champion));
    return Array.from(set).sort();
  }, [games]);

  const oppChampions = useMemo(() => {
    const set = new Set(games.filter(g => g.opponentStats).map(g => g.opponentStats.champion));
    return Array.from(set).sort();
  }, [games]);

  // Games filtrées
  const filteredGames = useMemo(() => {
    return games.filter(g => {
      if (!g.opponentStats) return false;
      if (filterMyChampion !== 'all' && g.champion !== filterMyChampion) return false;
      if (filterOppChampion !== 'all' && g.opponentStats.champion !== filterOppChampion) return false;
      if (filterResult !== 'all' && g.result !== filterResult) return false;
      return true;
    });
  }, [games, filterMyChampion, filterOppChampion, filterResult]);

  const data = useMemo(() => {
    const gamesWithOpp = filteredGames;
    if (gamesWithOpp.length === 0) return null;

    // V6.1 : Helper pour récupérer les stats à la fenêtre choisie
    // Si windowMinute = 'full' on utilise les stats finales de game
    // Sinon on utilise les timeFrames à 10/15/20min
    const getStatsAtWindow = (g) => {
      if (windowMinute === 'full') {
        return {
          myCs: g.csPerMin || 0,
          oppCs: g.opponentStats?.csPerMin || 0,
          myDmg: g.duration > 0 ? (g.damageDealt / g.duration) : 0,
          oppDmg: g.opponentStats?.damagePerMin || 0,
          myGold: g.opponentStats?.myGoldPerMin || 0,
          oppGold: g.opponentStats?.oppGoldPerMin || 0,
          hasFrame: true
        };
      }
      const key = `min${windowMinute}`;
      const f = g.timeFrames?.[key];
      if (!f) return { hasFrame: false };
      // Convertir en /min pour cohérence avec mode "full"
      const min = parseInt(windowMinute);
      return {
        myCs: +(f.my.cs / min).toFixed(2),
        oppCs: +(f.opp.cs / min).toFixed(2),
        myGold: Math.round(f.my.gold / min),
        oppGold: Math.round(f.opp.gold / min),
        myCsTotal: f.my.cs,
        oppCsTotal: f.opp.cs,
        myGoldTotal: f.my.gold,
        oppGoldTotal: f.opp.gold,
        myLvl: f.my.level,
        oppLvl: f.opp.level,
        hasFrame: true
      };
    };

    const totals = {
      myCs: 0, oppCs: 0,
      myDmg: 0, oppDmg: 0,
      myKills: 0, oppKills: 0,
      myDeaths: 0, oppDeaths: 0,
      myGold: 0, oppGold: 0,
      killsOnOpponent: 0, killsByOpponent: 0,
      games: 0,
      laneWins: 0,
      laneDraws: 0
    };

    let actualGamesCount = 0;
    gamesWithOpp.forEach(g => {
      const o = g.opponentStats;
      const stats = getStatsAtWindow(g);
      if (!stats.hasFrame) return;

      actualGamesCount++;
      totals.myCs += stats.myCs;
      totals.oppCs += stats.oppCs;
      // DMG seulement disponible en mode 'full' (pas dans timeFrames)
      if (windowMinute === 'full') {
        totals.myDmg += stats.myDmg;
        totals.oppDmg += stats.oppDmg;
      }
      totals.myGold += stats.myGold;
      totals.oppGold += stats.oppGold;
      totals.myKills += g.kills || 0;
      totals.oppKills += o.kills || 0;
      totals.myDeaths += g.deaths || 0;
      totals.oppDeaths += o.deaths || 0;
      totals.killsOnOpponent += o.killsOfMe || 0;
      totals.killsByOpponent += o.killsOnMe || 0;

      // v8.21.3 : seuils +0.5 / -0.5 (won / drawn / lost)
      const laneMinute = windowMinute === 'full' ? 15 : parseInt(windowMinute);
      const laneResult = isLaneWon(g, laneMinute, role);
      if (laneResult === 'won')  totals.laneWins++;
      if (laneResult === 'drawn') totals.laneDraws++;
    });

    if (actualGamesCount === 0) {
      return { noData: true, windowMinute };
    }
    totals.games = actualGamesCount;

    const n = actualGamesCount;
    const avg = {
      myCs: +(totals.myCs / n).toFixed(1),
      oppCs: +(totals.oppCs / n).toFixed(1),
      myDmg: Math.round(totals.myDmg / n),
      oppDmg: Math.round(totals.oppDmg / n),
      myKills: +(totals.myKills / n).toFixed(1),
      oppKills: +(totals.oppKills / n).toFixed(1),
      myDeaths: +(totals.myDeaths / n).toFixed(1),
      oppDeaths: +(totals.oppDeaths / n).toFixed(1),
      myGold: Math.round(totals.myGold / n),
      oppGold: Math.round(totals.oppGold / n)
    };

    // ====== TIME FRAMES AGGREGATION (table chronologique, pas affecté par windowMinute) ======
    const timestamps = [5, 10, 15, 20, 25, 30];
    const framesAgg = {};
    timestamps.forEach(min => {
      const key = `min${min}`;
      const gamesWithFrame = gamesWithOpp.filter(g => g.timeFrames && g.timeFrames[key]);
      if (gamesWithFrame.length === 0) return;

      let myCs = 0, oppCs = 0, myGold = 0, oppGold = 0, myXp = 0, oppXp = 0, myLvl = 0, oppLvl = 0;
      gamesWithFrame.forEach(g => {
        const f = g.timeFrames[key];
        myCs += f.my.cs;
        oppCs += f.opp.cs;
        myGold += f.my.gold;
        oppGold += f.opp.gold;
        myXp += f.my.xp;
        oppXp += f.opp.xp;
        myLvl += f.my.level;
        oppLvl += f.opp.level;
      });
      const c = gamesWithFrame.length;
      framesAgg[key] = {
        minute: min,
        games: c,
        myCs: Math.round(myCs / c),
        oppCs: Math.round(oppCs / c),
        csDiff: Math.round((myCs - oppCs) / c),
        myGold: Math.round(myGold / c),
        oppGold: Math.round(oppGold / c),
        goldDiff: Math.round((myGold - oppGold) / c),
        myXp: Math.round(myXp / c),
        oppXp: Math.round(oppXp / c),
        xpDiff: Math.round((myXp - oppXp) / c),
        myLvl: +(myLvl / c).toFixed(1),
        oppLvl: +(oppLvl / c).toFixed(1)
      };
    });

    // ====== MATCHUPS (fenêtrés selon windowMinute) ======
    const byOpponent = {};
    gamesWithOpp.forEach(g => {
      const opp = g.opponentStats.champion;
      const stats = getStatsAtWindow(g);
      if (!stats.hasFrame) return; // skip si pas de frame à cette fenêtre

      if (!byOpponent[opp]) {
        byOpponent[opp] = {
          champion: opp, games: 0,
          myCs: 0, oppCs: 0,
          myDmg: 0, oppDmg: 0,
          killsOn: 0, killsBy: 0,
          wins: 0
        };
      }
      const b = byOpponent[opp];
      b.games++;
      b.myCs += stats.myCs;
      b.oppCs += stats.oppCs;
      if (windowMinute === 'full') {
        b.myDmg += stats.myDmg;
        b.oppDmg += stats.oppDmg;
      }
      b.killsOn += g.opponentStats.killsOfMe || 0;
      b.killsBy += g.opponentStats.killsOnMe || 0;
      if (g.result === 'W') b.wins++;
    });

    const matchups = Object.values(byOpponent).map(b => ({
      ...b,
      avgCsDiff: +((b.myCs - b.oppCs) / b.games).toFixed(1),
      avgDmgDiff: windowMinute === 'full' ? Math.round((b.myDmg - b.oppDmg) / b.games) : null,
      kdDiff: b.killsOn - b.killsBy,
      winrate: Math.round(b.wins / b.games * 100)
    })).sort((a, b) => b.games - a.games);

    return {
      avg,
      totals,
      matchups,
      laneWinrate: Math.round((totals.laneWins + totals.laneDraws * 0.5) / n * 100),
      laneWins: totals.laneWins,
      laneDraws: totals.laneDraws,
      laneLosses: n - totals.laneWins - totals.laneDraws,
      framesAgg,
      windowMinute,
      hasDmg: windowMinute === 'full'
    };
  }, [filteredGames, windowMinute]);

  if (!data && games.filter(g => g.opponentStats).length === 0) {
    return (
      <EmptyState
        message="Pas de données opponent disponibles"
        sub="Importe des games via Riot API pour avoir les stats de l'ADC adverse"
      />
    );
  }

  const StatRow = ({ label, mine, them, higher = true, isLargeNum = false }) => {
    const winning = higher ? mine > them : mine < them;
    const diff = mine - them;
    return (
      <div className="grid grid-cols-[120px_1fr_auto_1fr] gap-3 items-center py-2.5 border-b border-zinc-800 last:border-0">
        <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider">{label}</div>
        <div className={`text-right font-mono font-bold ${winning ? 'text-cyan-400' : 'text-zinc-400'}`}>
          {isLargeNum ? mine.toLocaleString() : mine}
        </div>
        <div className="text-zinc-600 text-xs">vs</div>
        <div className={`font-mono font-bold ${!winning && diff !== 0 ? 'text-fuchsia-400' : 'text-zinc-400'}`}>
          {isLargeNum ? them.toLocaleString() : them}
        </div>
      </div>
    );
  };

  const framesArray = data?.framesAgg ? Object.values(data.framesAgg) : [];

  return (
    <div className="space-y-6">
      <SectionHeader
        title={roleLabel.screen}
        subtitle={`Toi vs ${roleLabel.opponent} · ${filteredGames.length} games · ${games.filter(g => g.opponentStats).length} avec data`}
        icon={Swords}
      />

      {/* Filtres */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Filter size={12} className="text-cyan-400" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-semibold">Filtres</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <div className="text-[10px] text-zinc-600 font-mono mb-1">MON CHAMPION</div>
            <select
              value={filterMyChampion}
              onChange={(e) => setFilterMyChampion(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs p-2 focus:border-cyan-400 focus:outline-none"
            >
              <option value="all">Tous mes champions</option>
              {myChampions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] text-zinc-600 font-mono mb-1">CHAMPION ADVERSE</div>
            <select
              value={filterOppChampion}
              onChange={(e) => setFilterOppChampion(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs p-2 focus:border-fuchsia-400 focus:outline-none"
            >
              <option value="all">Tous adversaires</option>
              {oppChampions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] text-zinc-600 font-mono mb-1">RÉSULTAT</div>
            <select
              value={filterResult}
              onChange={(e) => setFilterResult(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs p-2 focus:border-cyan-400 focus:outline-none"
            >
              <option value="all">W + L</option>
              <option value="W">Victoires</option>
              <option value="L">Défaites</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] text-zinc-600 font-mono mb-1 flex items-center gap-1">
              <Clock size={10} /> FENÊTRE LANE
            </div>
            <select
              value={windowMinute}
              onChange={(e) => setWindowMinute(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs p-2 focus:border-cyan-400 focus:outline-none"
            >
              <option value="full">Game complète</option>
              <option value="10">À 10 min</option>
              <option value="15">À 15 min</option>
              <option value="20">À 20 min</option>
            </select>
          </div>
        </div>
        {windowMinute !== 'full' && (
          <div className="text-[10px] text-cyan-400/70 font-mono mt-2 italic">
            Stats calculées au timing {windowMinute} min · DMG/min indisponible (utilise mode Game complète)
          </div>
        )}
      </Card>

      {!data && (
        <EmptyState message="Aucune game ne correspond aux filtres" />
      )}

      {data && data.noData && (
        <EmptyState
          message={`Pas de données disponibles pour la fenêtre ${data.windowMinute} min`}
          sub="Les games doivent avoir des timeFrames (importées via Riot API en v5.1+). Ré-importe tes games ou choisis 'Game complète'."
        />
      )}

      {data && !data.noData && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricTile label="Lane Winrate" value={data.laneWinrate} unit="%" subtext={data.laneWins !== undefined ? `${data.laneWins}W · ${data.laneDraws}D · ${data.laneLosses}L` : roleLabel.laneWin} color="cyan" />
            <MetricTile label="Kills directs" value={data.totals.killsOnOpponent} subtext="sur l'ADC adverse" color="cyan" />
            <MetricTile label="Tués par lui" value={data.totals.killsByOpponent} subtext="par l'ADC adverse" color="rose" />
            <MetricTile label="Diff K/D" value={data.totals.killsOnOpponent - data.totals.killsByOpponent} color={data.totals.killsOnOpponent >= data.totals.killsByOpponent ? 'cyan' : 'rose'} />
          </div>

          {/* Insight auto */}
          {data.laneWinrate >= 60 && (
            <Insight variant="success">
              <strong>Lane dominant</strong> — Tu surclasses ton adversaire dans {data.laneWinrate}% des games filtrées.
            </Insight>
          )}
          {data.laneWinrate < 40 && (
            <Insight variant="warning">
              <strong>⚠️ Lane phase à travailler</strong> — Tu sors de lane perdant dans {100 - data.laneWinrate}% des cas.
            </Insight>
          )}

          {/* Comparaison aux timers */}
          {framesArray.length > 0 && (
            <Card>
              <SectionHeader title="Comparaison aux timers" subtitle="Gold, CS, XP par minute clé (5/10/15/20...)" icon={Clock} />

              {/* Tableau timers — desktop */}
              <div className="hidden md:block overflow-x-auto mb-4">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left p-2 text-zinc-500 tracking-wider uppercase">Min</th>
                      <th className="text-right p-2 text-cyan-400">Mon CS</th>
                      <th className="text-right p-2 text-fuchsia-400">Adv CS</th>
                      <th className="text-right p-2 text-zinc-400">Diff CS</th>
                      <th className="text-right p-2 text-cyan-400">Mon Gold</th>
                      <th className="text-right p-2 text-fuchsia-400">Adv Gold</th>
                      <th className="text-right p-2 text-zinc-400">Diff Gold</th>
                      <th className="text-right p-2 text-amber-400">Diff XP</th>
                      <th className="text-right p-2 text-zinc-500">Mon Lvl</th>
                      <th className="text-right p-2 text-zinc-500">Adv Lvl</th>
                    </tr>
                  </thead>
                  <tbody>
                    {framesArray.map(f => (
                      <tr key={f.minute} className="border-b border-zinc-800/50">
                        <td className="p-2 text-white font-bold">{f.minute}'</td>
                        <td className="p-2 text-right text-cyan-400">{f.myCs}</td>
                        <td className="p-2 text-right text-fuchsia-400">{f.oppCs}</td>
                        <td className={`p-2 text-right font-bold ${f.csDiff > 0 ? 'text-cyan-400' : f.csDiff < 0 ? 'text-rose-400' : 'text-zinc-400'}`}>{f.csDiff > 0 ? '+' : ''}{f.csDiff}</td>
                        <td className="p-2 text-right text-cyan-400">{f.myGold.toLocaleString()}</td>
                        <td className="p-2 text-right text-fuchsia-400">{f.oppGold.toLocaleString()}</td>
                        <td className={`p-2 text-right font-bold ${f.goldDiff > 0 ? 'text-cyan-400' : f.goldDiff < 0 ? 'text-rose-400' : 'text-zinc-400'}`}>{f.goldDiff > 0 ? '+' : ''}{f.goldDiff}</td>
                        <td className={`p-2 text-right font-bold ${f.xpDiff > 0 ? 'text-amber-400' : f.xpDiff < 0 ? 'text-rose-400' : 'text-zinc-400'}`}>{f.xpDiff > 0 ? '+' : ''}{f.xpDiff}</td>
                        <td className="p-2 text-right text-zinc-400">{f.myLvl}</td>
                        <td className="p-2 text-right text-zinc-400">{f.oppLvl}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Tableau timers — mobile (cartes empilées) */}
              <div className="md:hidden space-y-2 mb-4">
                {framesArray.map(f => (
                  <div key={f.minute} className="border border-zinc-800 bg-zinc-900/50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-white font-bold text-base">{f.minute}'</div>
                      <div className="flex gap-3 text-[10px] font-mono">
                        <div>Mon Lvl: <span className="text-zinc-300">{f.myLvl}</span></div>
                        <div>Adv: <span className="text-zinc-300">{f.oppLvl}</span></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
                      <div>
                        <div className="text-[9px] text-zinc-500 tracking-wider uppercase">CS</div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-cyan-400">{f.myCs}</span>
                          <span className="text-zinc-600">vs</span>
                          <span className="text-fuchsia-400">{f.oppCs}</span>
                        </div>
                        <div className={`text-[10px] font-bold ${f.csDiff > 0 ? 'text-cyan-400' : f.csDiff < 0 ? 'text-rose-400' : 'text-zinc-400'}`}>
                          {f.csDiff > 0 ? '+' : ''}{f.csDiff}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[9px] text-zinc-500 tracking-wider uppercase">GOLD</div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-cyan-400">{f.myGold.toLocaleString()}</span>
                          <span className="text-zinc-600">vs</span>
                          <span className="text-fuchsia-400">{f.oppGold.toLocaleString()}</span>
                        </div>
                        <div className={`text-[10px] font-bold ${f.goldDiff > 0 ? 'text-cyan-400' : f.goldDiff < 0 ? 'text-rose-400' : 'text-zinc-400'}`}>
                          {f.goldDiff > 0 ? '+' : ''}{f.goldDiff}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Graph gold diff */}
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={framesArray}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="minute" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} tickFormatter={v => `${v}min`} />
                  <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'monospace' }} />
                  <ReferenceLine y={0} stroke="#52525b" strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="goldDiff" stroke={CHART_COLORS.amber} strokeWidth={2} name="Gold diff" />
                  <Line type="monotone" dataKey="csDiff" stroke={CHART_COLORS.cyan} strokeWidth={2} name="CS diff" />
                </LineChart>
              </ResponsiveContainer>
              <div className="text-[10px] text-zinc-500 font-mono text-center mt-2">
                Au-dessus de 0 = tu domines · En-dessous = tu te fais dominer
              </div>
            </Card>
          )}

          {/* Comparaison globale */}
          <Card>
            <SectionHeader title="Moyennes par game" subtitle="Performance moyenne en lane" />
            <div className="grid grid-cols-[120px_1fr_auto_1fr] gap-3 pb-2 border-b-2 border-zinc-800 mb-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">Métrique</div>
              <div className="text-right text-[10px] tracking-wider uppercase text-cyan-400 font-bold">Toi</div>
              <div></div>
              <div className="text-[10px] tracking-wider uppercase text-fuchsia-400 font-bold">Adversaire</div>
            </div>
            <StatRow label="CS/min" mine={data.avg.myCs} them={data.avg.oppCs} />
            {data.hasDmg && <StatRow label="DMG/min" mine={data.avg.myDmg} them={data.avg.oppDmg} isLargeNum />}
            <StatRow label="Kills" mine={data.avg.myKills} them={data.avg.oppKills} />
            <StatRow label="Deaths" mine={data.avg.myDeaths} them={data.avg.oppDeaths} higher={false} />
            <StatRow label="Gold" mine={data.avg.myGold} them={data.avg.oppGold} isLargeNum />
            {!data.hasDmg && (
              <div className="text-[10px] text-zinc-500 italic font-mono mt-2">
                DMG/min non disponible aux fenêtres temporelles · disponible en mode "Game complète"
              </div>
            )}
          </Card>

          {/* Par matchup */}
          <Card>
            <SectionHeader title="Performance par matchup" subtitle="Comment tu performes contre chaque ADC" />

            {/* Desktop : grid 7 cols */}
            <div className="hidden md:block space-y-1">
              {data.matchups.map(m => {
                const champ = champions?.find(c => c.name === m.champion || c.id === m.champion);
                const csColor = m.avgCsDiff > 0 ? 'text-cyan-400' : m.avgCsDiff < 0 ? 'text-rose-400' : 'text-zinc-400';
                const dmgColor = m.avgDmgDiff > 0 ? 'text-cyan-400' : m.avgDmgDiff < 0 ? 'text-rose-400' : 'text-zinc-400';
                const kdColor = m.kdDiff > 0 ? 'text-cyan-400' : m.kdDiff < 0 ? 'text-rose-400' : 'text-zinc-400';
                const gridCols = data.hasDmg
                  ? 'grid-cols-[auto_1fr_60px_80px_80px_80px_60px]'
                  : 'grid-cols-[auto_1fr_60px_80px_80px_60px]';
                return (
                  <div key={m.champion} className={`grid ${gridCols} gap-3 items-center border-l-2 border-zinc-800 hover:border-cyan-400/50 pl-3 py-2 transition`}>
                    {champ ? (
                      <img src={getChampionIconUrl(champ.id, version)} alt="" className="w-8 h-8 object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : <div className="w-8 h-8" />}
                    <div className="font-bold text-sm text-zinc-300">{m.champion}</div>
                    <div className="text-xs text-zinc-500 font-mono text-right">{m.games}G</div>
                    <div className={`font-mono text-xs text-right ${csColor}`} title="Diff CS/min">
                      {m.avgCsDiff > 0 ? '+' : ''}{m.avgCsDiff} cs
                    </div>
                    {data.hasDmg && (
                      <div className={`font-mono text-xs text-right ${dmgColor}`} title="Diff DMG/min">
                        {m.avgDmgDiff > 0 ? '+' : ''}{m.avgDmgDiff}
                      </div>
                    )}
                    <div className={`font-mono text-xs text-right ${kdColor}`} title="Kills sur lui - Tués par lui">
                      K/D {m.kdDiff > 0 ? '+' : ''}{m.kdDiff}
                    </div>
                    <div className={`font-mono text-sm font-bold text-right ${m.winrate >= 54 ? 'text-cyan-400' : 'text-rose-400'}`}>
                      {m.winrate}%
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile : cards empilées */}
            <div className="md:hidden space-y-2">
              {data.matchups.map(m => {
                const champ = champions?.find(c => c.name === m.champion || c.id === m.champion);
                const csColor = m.avgCsDiff > 0 ? 'text-cyan-400' : m.avgCsDiff < 0 ? 'text-rose-400' : 'text-zinc-400';
                const dmgColor = m.avgDmgDiff > 0 ? 'text-cyan-400' : m.avgDmgDiff < 0 ? 'text-rose-400' : 'text-zinc-400';
                const kdColor = m.kdDiff > 0 ? 'text-cyan-400' : m.kdDiff < 0 ? 'text-rose-400' : 'text-zinc-400';
                return (
                  <div key={m.champion} className="border border-zinc-800 bg-zinc-900/50 p-3">
                    <div className="flex items-center gap-3 mb-2">
                      {champ && <img src={getChampionIconUrl(champ.id, version)} alt="" className="w-9 h-9 object-cover flex-shrink-0" loading="lazy" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-white truncate">{m.champion}</div>
                        <div className="text-[10px] text-zinc-500 font-mono">{m.games} game{m.games > 1 ? 's' : ''}</div>
                      </div>
                      <div className={`font-mono text-xl font-bold ${m.winrate >= 54 ? 'text-cyan-400' : 'text-rose-400'}`}>
                        {m.winrate}%
                      </div>
                    </div>
                    <div className={`grid ${data.hasDmg ? 'grid-cols-3' : 'grid-cols-2'} gap-2 text-[11px] font-mono pt-2 border-t border-zinc-800`}>
                      <div>
                        <div className="text-[9px] text-zinc-500 tracking-wider uppercase">CS diff</div>
                        <div className={`font-bold ${csColor}`}>{m.avgCsDiff > 0 ? '+' : ''}{m.avgCsDiff}</div>
                      </div>
                      {data.hasDmg && (
                        <div>
                          <div className="text-[9px] text-zinc-500 tracking-wider uppercase">DMG diff</div>
                          <div className={`font-bold ${dmgColor}`}>{m.avgDmgDiff > 0 ? '+' : ''}{m.avgDmgDiff}</div>
                        </div>
                      )}
                      <div>
                        <div className="text-[9px] text-zinc-500 tracking-wider uppercase">K/D diff</div>
                        <div className={`font-bold ${kdColor}`}>{m.kdDiff > 0 ? '+' : ''}{m.kdDiff}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
